import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useState, useEffect } from 'react';

const NAV = [
  { label: 'nav.dashboard',         to: '/worker/dashboard',     icon: '🏠' },
  { label: 'nav.requirements',      to: '/worker/jobs',          icon: '💼' },
  { label: 'worker.job_matches',    to: '/worker/matches',       icon: '🎯' },
  { label: 'worker.my_applications', to: '/worker/applications', icon: '📋' },
  { label: 'nav.profile',           to: '/worker/profile',       icon: '👤' },
  { label: 'worker.digital_card',   to: '/worker/card',          icon: '🪪' },
];

export default function WorkerLayout({ children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const logout       = useAuthStore((s) => s.logout);
  const user         = useAuthStore((s) => s.user);
  const workerName   = useAuthStore((s) => s.workerName);
  const setWorkerName = useAuthStore((s) => s.setWorkerName);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    // workerName is null = never fetched this session; '' = fetched but no profile yet
    if (useAuthStore.getState().workerName !== null) return;
    api.get('/worker/profile')
      .then((res) => setWorkerName(res.data.data?.full_name || ''))
      .catch(() => setWorkerName(''));
  }, []);

  async function handleLogout() {
    try { await api.post('/auth/logout'); } catch {}
    logout();
    navigate('/login');
    toast.success('Logged out');
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Bar */}
      <header className="bg-brand-700 text-white sticky top-0 z-40 shadow">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/worker/dashboard" className="font-bold text-lg">naukarihaat.com</Link>
          <div className="flex items-center gap-3">
            <span className="text-sm hidden sm:block">{workerName || user?.mobile}</span>
            <button onClick={() => setMobileOpen(!mobileOpen)} className="sm:hidden p-1">☰</button>
            <button onClick={handleLogout} className="text-sm hidden sm:block hover:underline">{t('nav.logout')}</button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar desktop */}
        <aside className="hidden sm:flex flex-col w-56 bg-white border-r border-gray-200 py-6 px-4 gap-1 fixed top-14 bottom-0">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                location.pathname.startsWith(item.to)
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span>{item.icon}</span>
              {t(item.label)}
            </Link>
          ))}
          <div className="mt-auto">
            <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg w-full">
              🚪 {t('nav.logout')}
            </button>
          </div>
        </aside>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="sm:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setMobileOpen(false)}>
            <div className="bg-white w-64 h-full p-6 flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
              {NAV.map((item) => (
                <Link key={item.to} to={item.to} onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50">
                  {item.icon} {t(item.label)}
                </Link>
              ))}
              <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-3 text-red-600 rounded-lg mt-auto">
                🚪 {t('nav.logout')}
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 sm:ml-56 p-4 sm:p-6 max-w-5xl w-full pb-20 sm:pb-6">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex z-40">
        {NAV.slice(0, 4).map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`flex-1 flex flex-col items-center py-2 text-xs ${
              location.pathname.startsWith(item.to) ? 'text-brand-600' : 'text-gray-500'
            }`}
          >
            <span className="text-lg">{item.icon}</span>
            {t(item.label).split('.').pop()}
          </Link>
        ))}
      </nav>
    </div>
  );
}
